import {Injectable} from '@angular/core';
import {Actions, Effect, ofType} from '@ngrx/effects';
import {catchError, switchMap} from 'rxjs/operators';
import {from, of} from 'rxjs';
import {LoadError} from '@app/core/store/actions/error.actions';
import {PunchClockService} from '@app/core/services/api/punch-clock/punch-clock.service';
import {
	EPunchClockActions,
	GetPunchClockUsers,
	GetPunchClockUsersSuccess,
	GetPunchDayNotes,
	GetPunchDayNotesSuccess,
	GetPunchDepartment,
	GetPunchDepartmentSuccess,
	GetPunchTenant,
	OpenPunchModal,
	PunchInUser,
	PunchInUserSuccess,
	PunchOutUser,
	PunchOutUserSuccess,
	SetPunchPinCode,
	SetPunchPinCodeSuccess,
} from '@app/core/store/actions/punch-clock.actions';
import {Store} from '@ngrx/store';
import {IAppState} from '@app/core/store/state/app.state';
import {MatDialog} from '@angular/material/dialog';
import {PunchUserPopupComponent} from '@app/modules/punchUser/components/punch-user-popup/punch-user-popup.component';
import {PaginationHolder} from '@app/core/models/pagination.model';
import {DayNote} from '@app/core/models/day-note.model';
import {User} from '@app/core/models/user.model';
import {SnackBar} from '@app/core/services/snackbar.service';
import {TranslateService} from '@ngx-translate/core';
import {Department} from '@app/core/models/department.model';
import {AuthService} from '@app/core/services/auth.service';
import {InternationalizationService} from '@app/core/services/internationalization.service';
import {TenantService} from '@app/modules/shared/services/tenant.service';
import {Tenant} from '@app/core/models/tenant.model';
import {Router} from '@angular/router';
import {SetTenant} from '@app/core/store/actions/session.actions';
import {LocalStorageService} from '@app/core/services/local-storage.service';

@Injectable()
export class PunchClockEffects {

	@Effect()
	public onGetPunchClockUsersAndDepartments$ = this.actions$.pipe(
		ofType<GetPunchClockUsers>(EPunchClockActions.GetPunchClockUsers),
		switchMap((action: GetPunchClockUsers) => from(this.punchClockService.getPunchUsers(action.page_size, action.page, action.inSchedule, action.searchTerm)).pipe(
			switchMap((res: PaginationHolder<User>) => [
				new GetPunchClockUsersSuccess(res.data, res.pagination),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onPunchInUser$ = this.actions$.pipe(
		ofType<PunchInUser>(EPunchClockActions.PunchInUser),
		switchMap((action: PunchInUser) => from(this.punchClockService.punchInUser(action.userId, action.groupId, action.pinCode)).pipe(
			switchMap((user: User) => {
				this.dialog.closeAll();
				this.snackBar.newSuccess(this.translate.instant('SNACKBAR.BEEN_PUNCHED_IN'));

				return [
					new PunchInUserSuccess(user),
				];
			}),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onPunchOutUser$ = this.actions$.pipe(
		ofType<PunchOutUser>(EPunchClockActions.PunchOutUser),
		switchMap((action: PunchOutUser) => from(this.punchClockService.punchOutUser(action.userId, action.punchNote, action.pinCode)).pipe(
			switchMap((user: User) => {
				this.dialog.closeAll();
				this.snackBar.newSuccess(this.translate.instant('SNACKBAR.BEEN_PUNCHED_OUT'));

				return [
					new PunchOutUserSuccess(user),
				];
			}),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onGetDayNotes$ = this.actions$.pipe(
		ofType<GetPunchDayNotes>(EPunchClockActions.GetPunchDayNotes),
		switchMap((action: GetPunchDayNotes) => from(this.punchClockService.getDayNotes(action.page_size, action.page, action.date)).pipe(
			switchMap((res: PaginationHolder<DayNote>) =>
				[
					new GetPunchDayNotesSuccess(res.data, res.pagination),
				]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onGetPunchDepartment$ = this.actions$.pipe(
		ofType<GetPunchDepartment>(EPunchClockActions.GetPunchDepartment),
		switchMap((action: GetPunchDepartment) => from(this.punchClockService.getPunchDepartment(action.id)).pipe(
			switchMap((dep: Department) =>
				[
					new GetPunchDepartmentSuccess(dep),
				]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onOpenPunchModal$ = this.actions$.pipe(
		ofType<OpenPunchModal>(EPunchClockActions.OpenPunchModal),
		switchMap((action: OpenPunchModal) => {
			if (!(action.data.user?.groups?.length > 0)) {
				this.snackBar.newError(this.translate.instant('PUNCH_CLOCK.NO_GROUPS_FOUND'));

				return [];
			}
			this.dialog.open(PunchUserPopupComponent, {
				restoreFocus: false,
				id: 'punchUserModal',
				data: action.data,
				panelClass: 'fullscreen-dialog-max-s',
			});

			return [];
		}),
	);
	@Effect()
	public onGetPunchTenant$ = this.actions$.pipe(
		ofType<GetPunchTenant>(EPunchClockActions.GetPunchTenant),
		switchMap((action: GetPunchTenant) => from(this.punchClockService.getPunchTenant(action.id)).pipe(
			switchMap((tenant: Tenant) => {
				this.authService.tenant = this.tenantService.fresh(tenant);
				this.interService.timezone = tenant.timezone;
				this.interService.setLang(tenant.language.locale);

				this.localStorageService.storeAuthTenantId(tenant.id);

				this.store.dispatch(new SetTenant(tenant));
				this.router.navigate(['./punch-clock']);

				return [];
			}),
			catchError(error => of(new LoadError(error, action))),
		)),
	);
	@Effect()
	public onSetPunchPinCode$ = this.actions$.pipe(
		ofType<SetPunchPinCode>(EPunchClockActions.SetPunchPinCode),
		switchMap((action: SetPunchPinCode) => from(this.punchClockService.setPunchPinCode(action.id, action.data)).pipe(
			switchMap((res: User) => {
				this.dialog.getDialogById('punchNewPinModal')?.close({success: true, pinCode: action.data.pin_code});

				return [
					new SetPunchPinCodeSuccess(res),
				];
			}),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	constructor(
		private actions$: Actions,
		private punchClockService: PunchClockService,
		private store: Store<IAppState>,
		private dialog: MatDialog,
		private snackBar: SnackBar,
		private translate: TranslateService,
		private authService: AuthService,
		private interService: InternationalizationService,
		private tenantService: TenantService,
		private router: Router,
		private localStorageService: LocalStorageService
	) {
	}
}
